import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import ApiLifeFacts from "../../../api/life-facts";
import AdminResourceTable from "../components/layouts/general/admin-resource-table";
import { Body } from "../../../components/main-body/body";
import { useAdminResource } from "../components/hooks/useAdminResource";
import LifeFactsModal from "./components/life-facts-modal";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import { useParams, useLocation } from "react-router-dom";
import useNotification from "../../../components/notification/notification";
import { isReferencedError, hasReferencedFailure } from "../../../common/utils/referenced-error";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import DeleteModal from "../components/layouts/common/delete-modal";
import { useCan } from "../../../common/utils/usePermissions";

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");
  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const LifeFacts = ({ mode }) => {
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { id } = useParams();
  const location = useLocation();
  const { openNotification } = useNotification();
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  // ✅ init filters from URL (?statuses=pending)
  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const {
    form,
    list,
    active,
    update,
    open,
    close,
    createAndOpen,
    search,
    handleUpdateSearchValue,
    remove,
    tags,
    updateHasImage,
    updateIsUpdatedImage,
    authors,
    categories,
    factCitations,
    factUrls,
    isLoading,
    updateSorting,
    getResources,
    updateResourceSilent,
    hasMore,
    fetchNext,
    // ✅ IMPORTANT: take hook filter updater under alias
    updateFilterValue: updateFilterValueFromHook,
  } = useAdminResource(
    ApiLifeFacts,
    "facts",
    "lifefacts.notification",
    "fitfacts",
    "fact",
    "content"
  );

  // ✅ controlled updater: local state is source of truth,
  // but we also sync filters into the hook so they are sent to backend
  const updateFilterValue = useCallback(
    (value) => {
      const next = value || {};
      setFilters(next);
      updateFilterValueFromHook?.(next);
    },
    [updateFilterValueFromHook]
  );

  // ✅ on mount: sync initial URL-derived filters into the hook + refetch
  // This fixes the case when useAdminResource already did initial fetch without statuses.
  useEffect(() => {
    updateFilterValueFromHook?.(filters);
    getResources?.(); // 👈 IMPORTANT: force refetch with URL filters applied
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when filters change — refetch list (skip first run, because we refetch in mount effect above)
  const skipFirstFiltersRunRef = useRef(true);
  useEffect(() => {
    if (skipFirstFiltersRunRef.current) {
      skipFirstFiltersRunRef.current = false;
      return;
    }
    getResources?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // ✅ when URL changes (back/forward or manual navigation) — pull statuses from URL
  // and sync to both local filters + hook filters
  useEffect(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);

    const next = (() => {
      const prevStatuses = filters?.statuses || null;
      const nextStatuses = statusesFromUrl || null;

      const same =
        (prevStatuses === null && nextStatuses === null) ||
        (Array.isArray(prevStatuses) &&
          Array.isArray(nextStatuses) &&
          arraysEqual(prevStatuses, nextStatuses));

      if (same) return filters;

      const n = { ...(filters || {}) };
      if (nextStatuses) n.statuses = nextStatuses;
      else delete n.statuses;
      return n;
    })();

    if (next !== filters) {
      setFilters(next);
      updateFilterValueFromHook?.(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, updateFilterValueFromHook]);

  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [isToggleOpen, setToggleOpen] = useState(false);
  const [toggleItem, setToggleItem] = useState(null);

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } =
    useBulkSelection(list, { rowKey: "id" });

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiLifeFacts.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "items were" : "item was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: hasReferencedFailure(failed) ? (
            <p>Unable to delete this object because it is referenced by another object in the system.</p>
          ) : (
            <p>
              Unable to delete {failedIds.length}{" "}
              {plural ? "items" : "item"}
            </p>
          ),
        });
      }

      if (!failedIds.length) clearSelection();
      else setSelectedKeys(failedIds);

      await getResources?.();
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete FitFacts</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    getResources,
    clearSelection,
    setSelectedKeys,
  ]);

  const openDeleteConfirm = (item) => {
    setDeleteItem(item);
    setDeleteOpen(true);
  };
  const closeDeleteConfirm = () => {
    setDeleteOpen(false);
    setDeleteItem(null);
  };

  const openToggleConfirm = (item) => {
    setToggleItem(item);
    setToggleOpen(true);
  };
  const closeToggleConfirm = () => {
    setToggleOpen(false);
    setToggleItem(null);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await remove(deleteItem.id, deleteItem.title);
    } finally {
      closeDeleteConfirm();
      close();
    }
  };

  const toggleActive = async (item) => {
    try {
      await ApiLifeFacts.toggleStatus(item.id);
      openNotification?.({ type: "success", message: <p>Status changed</p> });
      await getResources?.();
    } catch (err) {
      if (isReferencedError(err)) {
        openNotification?.({
          type: "error",
          message: (
            <p>Unable to delete this object because it is referenced by another object in the system.</p>
          ),
        });
      } else {
        openNotification?.({
          type: "error",
          message: <p>Failed to change status</p>,
        });
      }
    }
  };

  const confirmToggle = async () => {
    if (!toggleItem) return;
    try {
      await toggleActive(toggleItem);
    } finally {
      closeToggleConfirm();
    }
  };

  const handlePublish = async (factId) => {
    try {
      await ApiLifeFacts.publish(factId);
      openNotification?.({
        type: "success",
        message: <p>FitFact has been published</p>,
      });
      await getResources?.();
    } catch {
      openNotification?.({
        type: "error",
        message: <p>Failed to publish FitFact</p>,
      });
    }
  };

  const handleDiscard = async (factId) => {
    try {
      await ApiLifeFacts.discard(factId);
      openNotification?.({ type: "success", message: <p>Changes discarded</p> });
      await getResources?.();
    } catch {
      openNotification?.({
        type: "error",
        message: <p>Failed to discard changes</p>,
      });
    }
  };

  useEffect(() => {
    if (id && list.length) {
      const currentItem = list.find((item) => String(item.id) === String(id));
      if (currentItem && String(active?.id) !== String(currentItem.id)) {
        open(currentItem, mode);
      }
    }
  }, [id, list, open, mode, active?.id]);

  useEffect(() => {
    if (mode === "create") createAndOpen();
  }, [mode, createAndOpen]);

  useEffect(() => {
    if (!id && active && mode !== "create") close();
  }, [location.pathname, id, active, mode, close]);

  const statuses = useMemo(
    () => [
      { id: "active", name: "Active" },
      { id: "pending", name: "Pending" },
      { id: "inactive", name: "Inactive" },
    ],
    []
  );

  const filterItems = [
    { name: "Tag", id: "tags", children: tags },
    {
      name: "Source URL",
      id: "factUrlIds",
      children: (factUrls || []).map((x) => ({
        name: x.title || x.source,
        id: x.id,
      })),
    },
    {
      name: "Source citation",
      id: "factCitationIds",
      children: (factCitations || []).map((x) => ({
        name: x.title || x.source,
        id: x.id,
      })),
    },
    { name: "Category", id: "categoryIds", children: categories },
    { name: "Author", id: "userIds", children: authors },
    // ✅ Status filter
    { name: "Status", id: "statuses", children: statuses },
  ];

  return (
    <Body
      header={{
        title: "FitFacts",
        create: canCreate && { label: "Create FitFact", onClick: createAndOpen, testID: "fact-create" },
        search: { value: search, onHandleChange: handleUpdateSearchValue },
      }}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={filterItems}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      <LifeFactsModal
        articleId={active?.id}
        form={form}
        data={active}
        onClose={close}
        onSave={update}
        tags={tags}
        updateHasImage={updateHasImage}
        updateIsUpdatedImage={updateIsUpdatedImage}
        authors={authors}
        categories={categories}
        factCitations={factCitations}
        factUrls={factUrls}
        isLoading={isLoading}
        mode={mode}
        onDiscard={handleDiscard}
        onPublish={handlePublish}
        onSaveSilent={updateResourceSilent}
        remove={(factId, title) => openDeleteConfirm({ id: factId, title })}
        toggleActive={(item) => openToggleConfirm(item)}
      />

      <AdminResourceTable
        data={list}
        onHandleRowClick={open}
        tableColumnKeys={["titleColorMark", "categories", "statusSummary", "updatedAt"]}
        isLoading={isLoading}
        updateSorting={updateSorting}
        hasMore={hasMore}
        fetchData={fetchNext}
        selectionColumn={getSelectionColumn()}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        }
      />

      <DeleteModal
        title={toggleItem ? (toggleItem.text.length > 40 ? toggleItem.text.slice(0, 80) + '…' : toggleItem.text) : '—'}
        question={"Are you sure you want to delete this FitFact?"}
        handleOk={confirmDelete}
        handleCancel={closeDeleteConfirm}
        isModalOpen={isDeleteOpen}
      />

      <DeleteModal
        title={toggleItem ? (toggleItem.text.length > 40 ? toggleItem.text.slice(0, 80) + '…' : toggleItem.text) : '—'}
        question={`Are you sure you want to ${
          toggleItem?.isActive ? "deactivate" : "activate"
        } this FitFact?`}
        handleOk={confirmToggle}
        handleCancel={closeToggleConfirm}
        isModalOpen={isToggleOpen}
      />
    </Body>
  );
};

export default LifeFacts;
